import { getSessionAuthentication, rms2BasePath, baseUrl } from '../common/Api';

interface FetchOptions {
    method: string;
    headers: Record<string, string>;
    body?: string;
}

function getHeaders(): Record<string, string> {
    const auth = getSessionAuthentication();
    return {
        'Content-Type': 'application/json',
        'Authorization': auth.token,
        'x-cleverapps-tenant': auth.tenantStringIdentifier,
    };
}

async function rms2Fetch<T>(path: string, options?: Partial<FetchOptions>): Promise<T> {
    const response = await fetch(rms2BasePath + path, {
        method: options?.method ?? 'GET',
        headers: getHeaders(),
        body: options?.body,
    });
    if (!response.ok) {
        throw new Error(`API error: ${response.status} ${response.statusText}`);
    }
    const text = await response.text();
    return text ? JSON.parse(text) : null;
}

async function legacyFetch<T>(path: string, options?: Partial<FetchOptions>): Promise<T> {
    const response = await fetch(baseUrl + path, {
        method: options?.method ?? 'GET',
        headers: getHeaders(),
        body: options?.body,
    });
    if (!response.ok) {
        throw new Error(`API error: ${response.status} ${response.statusText}`);
    }
    const text = await response.text();
    return text ? JSON.parse(text) : null;
}

// Types

export interface Brand {
    id: number;
    brandCode: string;
    brandDescription: string;
    isActive: boolean;
    useAlternativeArticleNumber: boolean;
    useSupplierArticleNumber: boolean;
    isGroupSaleBrand: boolean;
    company: Company | null;
    companies: Company[];
    brandCategory: BrandCategory | null;
    barcodeType: BarcodeType | null;
    salesChannels: SalesChannel[];
    itemGroups: ItemGroup[];
    brandConnectIdentifier: string | null;
    brandConnect: BrandConnectInfo | null;
}

export interface BrandConnectInfo {
    supplierTenant: string;
    supplierName: string;
    status: string;
    connectedLocations: string[] | null;
}

export interface Company {
    id: number;
    companyName: string;
}

export interface BrandCategory {
    id: number;
    typeName: string;
    isBarcodeGenerationAllowed: boolean;
}

export interface BarcodeType {
    id: number;
    barcodeTypeName: string;
}

export interface SalesChannel {
    id: number;
    salesChannelCode: string;
    salesChannelDescription: string;
}

export interface MainGroup {
    id: number;
    mainGroupCode: string;
    mainGroupDescription: string;
}

export interface ItemGroup {
    mainGroup: MainGroup;
    showEditForm?: boolean;
}

export interface Location {
    id: number;
    locationNumber: number;
    locationName: string;
}

export interface TenantConfig {
    articleBrandCodeLength: number;
    assignedBrandCodes: string[];
}

export interface VariantGroup {
    id: number;
    variantGroupName: string;
}

export interface Vat {
    id: number;
    vatPercent: number;
}

// API calls

export async function getBrand(brandId: number): Promise<Brand> {
    return rms2Fetch<Brand>(`brands/${brandId}`);
}

export async function getCompanies(): Promise<Company[]> {
    return legacyFetch<Company[]>('brands/companies');
}

export async function getBrandCategories(): Promise<BrandCategory[]> {
    return legacyFetch<BrandCategory[]>('brands/brandcategories');
}

export async function getBarcodeTypes(): Promise<BarcodeType[]> {
    return legacyFetch<BarcodeType[]>('brands/barcodetypes');
}

export async function getSalesChannels(): Promise<SalesChannel[]> {
    return legacyFetch<SalesChannel[]>('brands/saleschannels');
}

export async function getMainGroups(): Promise<MainGroup[]> {
    return legacyFetch<MainGroup[]>('brands/maingroups');
}

export async function getLocations(): Promise<Location[]> {
    return legacyFetch<Location[]>('locations/brief');
}

export async function getTenantConfig(): Promise<TenantConfig> {
    return legacyFetch<TenantConfig>('brands/tenantconfig');
}

export async function getVariantGroups(): Promise<VariantGroup[]> {
    return legacyFetch<VariantGroup[]>('brands/variantgroups');
}

export async function isBrandConnectEnabled(): Promise<boolean> {
    try {
        const response = await rms2Fetch<boolean>('features/FeatureBrandConnect');
        return response ?? false;
    } catch {
        return false;
    }
}

export async function updateBrand(brand: Brand, mainGroups: MainGroup[], addedCompanies: Company[], removedCompanies: Company[]): Promise<Brand> {
    return legacyFetch<Brand>('brands', {
        method: 'PUT',
        body: JSON.stringify({ brand, mainGroups, addedCompanies, removedCompanies }),
    });
}

export async function createBrand(brand: Partial<Brand>, mainGroups: MainGroup[], addedCompanies: Company[], removedCompanies: Company[]): Promise<Brand> {
    return legacyFetch<Brand>('brands', {
        method: 'POST',
        body: JSON.stringify({ brand, mainGroups, addedCompanies, removedCompanies }),
    });
}

export async function createItemGroups(mainGroups: MainGroup[], brands: Brand[]): Promise<void> {
    await legacyFetch<void>('brands/createitemgroups', {
        method: 'PUT',
        body: JSON.stringify({ mainGroups, brands }),
    });
}

export async function connectBrandSupplier(brandId: number, supplierTenant: string, locationIds: number[]): Promise<void> {
    await rms2Fetch<void>(`brands/${brandId}/brandconnect/supplier`, {
        method: 'POST',
        body: JSON.stringify({ supplierTenant, locationIds }),
    });
}

export async function disconnectBrandSupplier(brandId: number): Promise<void> {
    await rms2Fetch<void>(`brands/${brandId}/brandconnect/supplier`, {
        method: 'DELETE',
    });
}

export async function setBrandConnectIdentifier(brandId: number, brandConnectIdentifier: string): Promise<void> {
    await rms2Fetch<void>(`brands/${brandId}/brandconnect/identifier`, {
        method: 'PUT',
        body: JSON.stringify({ brandConnectIdentifier }),
    });
}
